import {
  Watch,
  Battery,
  Wifi,
  WifiOff,
  RefreshCw,
  Settings,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import type { MonitoredPatient } from "@/hooks/useRemoteMonitoring";

interface DeviceStatusCardProps {
  patient: MonitoredPatient;
}

export function DeviceStatusCard({ patient }: DeviceStatusCardProps) {
  const getBatteryColor = (battery: number) => {
    if (battery > 50) return "text-success";
    if (battery > 20) return "text-warning";
    return "text-destructive";
  };

  const getSignalIcon = (signal: string) => {
    switch (signal) {
      case "strong":
        return <Wifi className="h-4 w-4 text-success" />;
      case "medium":
        return <Wifi className="h-4 w-4 text-warning" />;
      case "weak":
        return <WifiOff className="h-4 w-4 text-destructive" />;
      default:
        return <Wifi className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <Card className={cn(
      "transition-all hover:shadow-md",
      !patient.device.connected && "opacity-60"
    )}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary/10 text-primary">
                {patient.name.split(" ").map(n => n[0]).join("")}
              </AvatarFallback>
            </Avatar>
            <div>
              <h4 className="font-semibold">{patient.name}</h4>
              <p className="text-sm text-muted-foreground">{patient.device.type}</p>
            </div>
          </div>
          <span className="text-2xl">{patient.device.icon}</span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Connection Status */}
        <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
          <span className="text-sm font-medium">Connection Status</span>
          {patient.device.connected ? (
            <Badge className="gap-1 bg-success/10 text-success hover:bg-success/20">
              <CheckCircle2 className="h-3 w-3" />
              Connected
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1 text-muted-foreground">
              <AlertCircle className="h-3 w-3" />
              Disconnected
            </Badge>
          )}
        </div>

        {/* Device Metrics */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg border p-3">
            <div className="flex items-center gap-2 mb-1">
              <Battery className={cn("h-4 w-4", getBatteryColor(patient.device.battery))} />
              <span className="text-xs text-muted-foreground">Battery</span>
            </div>
            <p className={cn("text-lg font-bold", getBatteryColor(patient.device.battery))}>
              {patient.device.battery}%
            </p>
          </div>
          <div className="rounded-lg border p-3">
            <div className="flex items-center gap-2 mb-1">
              {getSignalIcon(patient.device.signal)}
              <span className="text-xs text-muted-foreground">Signal</span>
            </div>
            <p className="text-lg font-bold capitalize">{patient.device.signal}</p>
          </div>
        </div>

        {/* Last Sync */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Last synced</span>
          <span className="font-medium">{patient.device.lastSync}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1">
            <RefreshCw className="h-4 w-4 mr-1" />
            Sync Now
          </Button>
          <Button variant="ghost" size="sm">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
